import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [paymentStats, setPaymentStats] = useState({});

  useEffect(() => {
    loadPayments();
    loadPaymentStats();
  }, [filterStatus, dateRange]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/payments?status=${filterStatus}&dateRange=${dateRange}`);
      setPayments(response.data.payments || []);
    } catch (error) {
      toast.error('Error loading payments');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentStats = async () => {
    try {
      const response = await api.get('/admin/payment-stats');
      setPaymentStats(response.data.stats || {});
    } catch (error) {
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { bg: '#948979', text: 'white', icon: 'fa-check-circle' },
      pending: { bg: '#DFD0B8', text: '#393E46', icon: 'fa-clock' },
      failed: { bg: '#dc2626', text: 'white', icon: 'fa-times-circle' },
      refunded: { bg: '#393E46', text: 'white', icon: 'fa-undo' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span 
        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-md"
        style={{ backgroundColor: config.bg, color: config.text }}
      >
        <i className={`fas ${config.icon} mr-1`}></i>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPlanBadge = (planType) => {
    const planConfig = {
      basic: { bg: '#222831', text: 'white' },
      starter: { bg: '#948979', text: 'white' },
      pro: { bg: '#393E46', text: 'white' }
    };

    const config = planConfig[planType] || planConfig.basic;

    return (
      <span 
        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-md"
        style={{ backgroundColor: config.bg, color: config.text }}
      >
        {planType.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4" 
               style={{ borderColor: '#DFD0B8', borderTopColor: '#948979' }}></div>
          <p className="font-medium" style={{ color: '#393E46' }}>Loading payment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-6 lg:space-y-8">
      {/* Enhanced Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 lg:mb-8 gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-credit-card text-white text-lg lg:text-xl"></i>
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold truncate" style={{ color: '#222831' }}>
              Payment Management
            </h2>
          </div>
          <p className="ml-13 lg:ml-15 text-sm lg:text-base" style={{ color: '#393E46' }}>Track all payment transactions and revenue</p>
        </div>
      </div>

      {/* Enhanced Payment Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Total Revenue</p>
              <p className="text-2xl lg:text-3xl font-bold" style={{ color: '#948979' }}>₹{paymentStats.totalRevenue || 0}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#948979' }}></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>All time earnings</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-rupee-sign text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Today's Revenue</p>
              <p className="text-2xl lg:text-3xl font-bold" style={{ color: '#22c55e' }}>₹{paymentStats.todayEarnings || 0}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>Revenue earned today</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 bg-green-500 rounded-2xl flex items-center justify-center shadow-lg">
              <i className="fas fa-calendar-day text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Total Transactions</p>
              <p className="text-2xl lg:text-3xl font-bold" style={{ color: '#222831' }}>{paymentStats.totalTransactions || 0}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: '#222831' }}></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>Payment count</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
              <i className="fas fa-credit-card text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Pending Payments</p>
              <p className="text-2xl lg:text-3xl font-bold text-orange-600">{paymentStats.pendingPayments || 0}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>Awaiting completion</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
              <i className="fas fa-clock text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-4 lg:p-6 border-2 hover:shadow-2xl transition-all duration-300" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-semibold mb-2" style={{ color: '#393E46' }}>Failed Payments</p>
              <p className="text-2xl lg:text-3xl font-bold text-red-600">{paymentStats.failedPayments || 0}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                <span className="text-xs font-medium" style={{ color: '#393E46' }}>Unsuccessful attempts</span>
              </div>
            </div>
            <div className="w-12 h-12 lg:w-16 lg:h-16 bg-red-500 rounded-2xl flex items-center justify-center shadow-lg">
              <i className="fas fa-times-circle text-white text-lg lg:text-2xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Revenue Details */}
      <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}></div>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#22c55e' }}>
            <i className="fas fa-chart-pie text-white text-lg"></i>
          </div>
          <h3 className="text-xl lg:text-2xl font-bold" style={{ color: '#222831' }}>Revenue Breakdown</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Total Revenue */}
          <div className="text-center p-6 rounded-2xl border-2" style={{ backgroundColor: '#f0fdf4', borderColor: '#22c55e' }}>
            <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="fas fa-piggy-bank text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-green-700 mb-2">Total Revenue</h4>
            <p className="text-3xl font-bold text-green-600 mb-2">₹{paymentStats.totalRevenue || 0}</p>
            <p className="text-sm text-green-600">All time revenue from payments</p>
          </div>

          {/* Today's Revenue */}
          <div className="text-center p-6 rounded-2xl border-2" style={{ backgroundColor: '#eff6ff', borderColor: '#3b82f6' }}>
            <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="fas fa-calendar-day text-white text-2xl"></i>
            </div>
            <h4 className="text-lg font-bold text-blue-700 mb-2">Today's Revenue</h4>
            <p className="text-3xl font-bold text-blue-600 mb-2">₹{paymentStats.todayEarnings || 0}</p>
            <p className="text-sm text-blue-600">Revenue earned today</p>
          </div>
        </div>

        {/* Revenue Summary */}
        <div className="mt-8 p-6 rounded-2xl border-2" style={{ backgroundColor: '#DFD0B8', borderColor: '#948979' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h4 className="text-lg font-bold mb-2" style={{ color: '#222831' }}>Revenue Summary</h4>
              <p className="text-sm" style={{ color: '#393E46' }}>
                Total platform revenue of ₹{paymentStats.totalRevenue || 0} from {paymentStats.totalTransactions || 0} transactions.
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Total Transactions</p>
                  <p className="text-xl font-bold" style={{ color: '#948979' }}>{paymentStats.totalTransactions || 0}</p>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                  <i className="fas fa-arrow-right text-white text-sm"></i>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Total Revenue</p>
                  <p className="text-xl font-bold text-green-600">₹{paymentStats.totalRevenue || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters */}
      <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
            <i className="fas fa-filter text-white text-sm"></i>
          </div>
          <h3 className="text-xl font-bold" style={{ color: '#222831' }}>Filter Payments</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Status Filter</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-6 py-4 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831',
                focusRingColor: '#948979'
              }}
            >
              <option value="all">All Payments</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-6 py-4 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
            </select>
          </div>
        </div>
      </div>

      {/* Enhanced Payments List */}
      <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl overflow-hidden border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="p-6 border-b-2" style={{ backgroundColor: '#DFD0B8', borderColor: '#948979' }}>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-list text-white text-sm"></i>
            </div>
            <h3 className="text-xl font-bold" style={{ color: '#222831' }}>Payment Transactions</h3>
          </div>
        </div>

        {payments.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-credit-card text-4xl" style={{ color: '#948979' }}></i>
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#222831' }}>No payments found</h3>
            <p style={{ color: '#393E46' }}>No payments match the current filter criteria</p>
          </div>
        ) : (
          <div className="divide-y-2" style={{ borderColor: '#DFD0B8' }}>
            {payments.map((payment) => (
              <div key={payment._id} className="p-4 lg:p-6 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div className="flex items-center space-x-3 lg:space-x-4 flex-1 min-w-0">
                    {/* User Avatar */}
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center text-white font-bold shadow-lg flex-shrink-0" style={{ backgroundColor: '#948979' }}>
                      {payment.user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    
                    {/* Payment Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-2">
                        <h4 className="font-bold text-base lg:text-lg truncate" style={{ color: '#222831' }}>
                          {payment.user?.name || 'Unknown User'}
                        </h4>
                        <div className="flex flex-wrap gap-2 mt-1 sm:mt-0">
                          {getPlanBadge(payment.planType)}
                          {getStatusBadge(payment.status)}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs lg:text-sm" style={{ color: '#393E46' }}>
                        <span className="font-medium">₹{payment.amount}</span>
                        <span>•</span>
                        <span>{payment.listingsGranted} listings</span>
                        <span>•</span>
                        <span>{new Date(payment.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="text-xs mt-1 truncate" style={{ color: '#393E46' }}>
                        Payment ID: {payment.paymentId}
                      </div>
                    </div>
                  </div>

                  {/* Payment Amount */}
                  <div className="text-center sm:text-right flex-shrink-0">
                    <div className="text-xl lg:text-2xl font-bold mb-1" style={{ color: '#948979' }}>
                      ₹{payment.amount}
                    </div>
                    <div className="text-xs lg:text-sm font-medium" style={{ color: '#393E46' }}>
                      {payment.method || 'Unknown'}
                    </div>
                  </div>
                </div>

                {payment.failureReason && (
                  <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200">
                    <div className="flex items-center space-x-2">
                      <i className="fas fa-exclamation-triangle text-red-500"></i>
                      <span className="text-sm font-medium text-red-700">Failure Reason:</span>
                    </div>
                    <p className="text-sm text-red-600 mt-1 break-words">{payment.failureReason}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Revenue Chart Placeholder */}
      <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
            <i className="fas fa-chart-line text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>Revenue Analytics</h3>
        </div>
        <div className="h-64 flex items-center justify-center border-2 border-dashed rounded-2xl" style={{ borderColor: '#DFD0B8' }}>
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-chart-line text-3xl" style={{ color: '#948979' }}></i>
            </div>
            <h4 className="text-lg font-bold mb-2" style={{ color: '#222831' }}>Revenue Trends Coming Soon</h4>
            <p style={{ color: '#393E46' }}>Advanced revenue analytics and charts will be displayed here</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentManagement;